import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ChevronUp, ArrowLeft, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "../api";
import { Spinner, Pill, relTime } from "../ui";

const stageLabel = { OPENED: "In review", ANALYZED: "Analyzed", DECIDED: "Decided", RESOLVED: "Resolved" };

function Section({ title, children, testid }) {
  if (!children) return null;
  return (
    <section data-testid={testid} className="border-t border-white/[0.07] pt-8 mt-8">
      <h2 className="font-mono-label text-[10px] tracking-[0.3em] uppercase text-[#B5A58A]/80 mb-5">{title}</h2>
      {children}
    </section>
  );
}

function KV({ obj }) {
  if (!obj) return null;
  if (typeof obj === "string") return <p className="text-sm leading-relaxed text-stone-300">{obj}</p>;
  return (
    <div className="space-y-3">
      {Object.entries(obj).map(([k, v]) => {
        if (v == null || v === "") return null;
        if (Array.isArray(v)) {
          return (
            <div key={k}>
              <p className="font-mono-label text-[9px] tracking-[0.2em] uppercase text-stone-500 mb-2">{k.replace(/_/g, " ")}</p>
              <ul className="space-y-1.5">
                {v.map((item, i) => (
                  <li key={i} className="flex gap-3 text-sm leading-relaxed text-stone-300">
                    <span className="mt-[9px] w-1 h-1 rounded-full bg-[#B5A58A]/70 shrink-0" />
                    {typeof item === "string" ? item : item.content || item.title || JSON.stringify(item)}
                  </li>
                ))}
              </ul>
            </div>
          );
        }
        if (typeof v === "object") return null;
        return (
          <div key={k}>
            <p className="font-mono-label text-[9px] tracking-[0.2em] uppercase text-stone-500 mb-1.5">{k.replace(/_/g, " ")}</p>
            <p className="text-sm leading-relaxed text-stone-300">{String(v)}</p>
          </div>
        );
      })}
    </div>
  );
}

export default function ArchiveDetail() {
  const { pcn } = useParams();
  const [kase, setKase] = useState(null);
  const [comments, setComments] = useState(null);
  const [draft, setDraft] = useState("");
  const [upvoted, setUpvoted] = useState(false);
  const [count, setCount] = useState(0);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setKase(null);
    setNotFound(false);
    api.get(`/archive/${pcn}`)
      .then((r) => {
        setKase(r.data);
        setCount(r.data.upvote_count || 0);
        setUpvoted(r.data.has_upvoted || false);
      })
      .catch(() => setNotFound(true));
    api.get(`/archive/${pcn}/comments`).then((r) => setComments(r.data.items || r.data.comments || [])).catch(() => setComments([]));
  }, [pcn]);

  const toggleUpvote = async () => {
    setUpvoted(!upvoted);
    setCount((c) => c + (upvoted ? -1 : 1));
    try {
      const r = await api.post(`/archive/${pcn}/upvote`);
      if (typeof r.data.upvote_count === "number") setCount(r.data.upvote_count);
      if (typeof r.data.upvoted === "boolean") setUpvoted(r.data.upvoted);
    } catch {
      setUpvoted(upvoted);
      setCount(count);
    }
  };

  const postComment = async () => {
    const content = draft.trim();
    if (content.length < 2 || content.length > 1200) return;
    try {
      const r = await api.post(`/archive/${pcn}/comments`, { content });
      setComments((cs) => [...(cs || []), r.data]);
      setDraft("");
      toast.success("Perspective added.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not post the comment.");
    }
  };

  const removeComment = async (cid) => {
    try {
      await api.delete(`/archive/${pcn}/comments/${cid}`);
      setComments((cs) => cs.filter((c) => (c.id || c._id) !== cid));
    } catch {
      toast.error("Only your own comments can be removed.");
    }
  };

  if (notFound)
    return (
      <div data-testid="archive-not-found" className="py-20 text-center">
        <p className="font-serif-display text-3xl text-white mb-4">This case is no longer public.</p>
        <Link to="/app/archive" className="font-mono-label text-[10px] tracking-[0.2em] uppercase text-[#B5A58A]">Back to the archive</Link>
      </div>
    );
  if (!kase) return <Spinner label="Reading the file…" />;

  const findings = kase.brief?.findings || [];
  const grouped = findings.reduce((acc, f) => {
    (acc[f.category] = acc[f.category] || []).push(f.content);
    return acc;
  }, {});

  return (
    <div data-testid="archive-detail-page" className="max-w-3xl pb-32">
      <Link to="/app/archive" data-testid="archive-back" className="inline-flex items-center gap-2 font-mono-label text-[10px] tracking-[0.2em] uppercase text-stone-400 hover:text-white transition-colors duration-300 mb-8">
        <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} /> Archive
      </Link>

      <div className="flex items-start justify-between gap-6 mb-3">
        <p className="font-mono-label text-[10px] tracking-[0.25em] text-[#B5A58A]/80">{kase.public_case_number}</p>
        <button
          data-testid="archive-upvote-btn"
          onClick={toggleUpvote}
          className={`flex items-center gap-2 border rounded-full px-4 py-2 font-mono-label text-[10px] tracking-[0.15em] transition-colors duration-300 ${
            upvoted ? "border-[#B5A58A] text-[#B5A58A]" : "border-white/[0.12] text-stone-400 hover:text-white"
          }`}
        >
          <ChevronUp className="w-4 h-4" strokeWidth={1.5} /> {count}
        </button>
      </div>
      <h1 className="font-serif-display font-light tracking-tighter leading-[1] text-4xl md:text-6xl text-white mb-4">{kase.title}</h1>
      <div className="flex items-center gap-3 mb-4">
        <Pill tone="accent">{stageLabel[kase.stage] || kase.stage}</Pill>
        <span className="font-mono-label text-[9px] tracking-[0.15em] uppercase text-stone-500">
          {kase.advisor?.name} · {relTime(kase.updated_at)}
        </span>
      </div>

      <Section title="Situation" testid="ad-situation">
        <p className="text-sm md:text-base leading-relaxed text-stone-300">{kase.situation}</p>
      </Section>

      {Object.keys(grouped).length > 0 && (
        <Section title="Case brief" testid="ad-brief">
          <div className="space-y-5">
            {Object.entries(grouped).map(([cat, items]) => (
              <div key={cat}>
                <p className="font-mono-label text-[9px] tracking-[0.25em] uppercase text-[#B5A58A]/70 mb-2">{cat}</p>
                <ul className="space-y-1.5">
                  {items.map((it, i) => (
                    <li key={i} className="flex gap-3 text-sm leading-relaxed text-stone-300">
                      <span className="mt-[9px] w-1 h-1 rounded-full bg-[#B5A58A]/70 shrink-0" />{it}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </Section>
      )}

      {(kase.messages || []).length > 0 && (
        <Section title="Interview" testid="ad-interview">
          <div className="space-y-4">
            {kase.messages.map((m, i) => (
              <div key={i} className={`rounded-2xl px-5 py-4 text-sm leading-relaxed ${m.role === "user" ? "bg-[#B5A58A]/10 text-stone-200 ml-8" : "bg-[#26241F] border border-white/[0.08] text-stone-300 mr-8"}`}>
                <p className="font-mono-label text-[8px] tracking-[0.2em] uppercase text-stone-500 mb-1.5">{m.role === "user" ? "Case owner" : kase.advisor?.name || "Advisor"}</p>
                {m.content}
              </div>
            ))}
          </div>
        </Section>
      )}

      {kase.analysis && <Section title="Analysis" testid="ad-analysis"><KV obj={kase.analysis} /></Section>}

      {(kase.advisory_board || []).length > 0 && (
        <Section title="Advisory board" testid="ad-board">
          <div className="space-y-4">
            {kase.advisory_board.map((a, i) => (
              <div key={i} className="bg-[#26241F] border border-white/[0.08] rounded-2xl p-6">
                <p className="font-serif-display text-xl text-white mb-2">{a.role_name}</p>
                <p className="text-sm leading-relaxed text-stone-400">{a.main_observation}</p>
              </div>
            ))}
          </div>
        </Section>
      )}

      {(kase.paths || []).length > 0 && (
        <Section title="Paths" testid="ad-paths">
          <div className="space-y-4">
            {kase.paths.map((p, i) => (
              <div key={i} className="bg-[#26241F] border border-white/[0.08] rounded-2xl p-6">
                <Pill tone={p.path_type === "BOLD" ? "warning" : p.path_type === "CAUTIOUS" ? "neutral" : "accent"}>{p.path_type}</Pill>
                <p className="font-serif-display text-xl text-white mt-3 mb-2">{p.title}</p>
                <p className="text-sm leading-relaxed text-stone-400">{p.description}</p>
              </div>
            ))}
          </div>
        </Section>
      )}

      {kase.decision && <Section title="Decision" testid="ad-decision"><KV obj={kase.decision} /></Section>}
      {kase.resolved_summary && (
        <Section title="Outcome" testid="ad-outcome">
          <p className="text-sm leading-relaxed text-stone-300">{typeof kase.resolved_summary === "string" ? kase.resolved_summary : JSON.stringify(kase.resolved_summary)}</p>
        </Section>
      )}

      <div data-testid="archive-comments" className="border-t border-white/[0.07] pt-8 mt-8">
        <h2 className="font-mono-label text-[10px] tracking-[0.3em] uppercase text-[#B5A58A]/80 mb-5">
          Perspectives ({(comments || []).length})
        </h2>
        {!comments ? (
          <Spinner label="Loading…" />
        ) : (
          <div className="space-y-4">
            {comments.map((c) => (
              <div key={c.id || c._id} className="bg-[#26241F] border border-white/[0.08] rounded-2xl p-5">
                <div className="flex items-center justify-between mb-2">
                  <p className="font-mono-label text-[9px] tracking-[0.2em] uppercase text-stone-500">
                    {c.anon_display || "Anonymous"} · {relTime(c.created_at)}
                  </p>
                  {c.own && (
                    <button data-testid={`comment-delete-${c.id || c._id}`} onClick={() => removeComment(c.id || c._id)} className="text-stone-500 hover:text-white transition-colors duration-300">
                      <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                    </button>
                  )}
                </div>
                <p className="text-sm leading-relaxed text-stone-300">{c.content}</p>
              </div>
            ))}
            {comments.length === 0 && <p className="text-sm text-stone-500">No perspectives yet. Yours could be the first.</p>}
          </div>
        )}
      </div>

      <div className="fixed bottom-0 left-0 lg:left-64 right-0 bg-[#1F1E1B]/95 backdrop-blur-md border-t border-white/[0.08] px-5 md:px-10 py-4">
        <div className="max-w-3xl flex items-end gap-3">
          <textarea
            data-testid="comment-input"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={2}
            maxLength={1200}
            placeholder="Add an anonymous perspective…"
            className="flex-1 bg-[#26241F] border border-white/[0.08] rounded-xl px-5 py-3 text-sm text-white placeholder:text-stone-500 outline-none focus:border-[#B5A58A]/50 transition-colors duration-300 resize-none"
          />
          <button
            data-testid="comment-submit"
            onClick={postComment}
            disabled={draft.trim().length < 2}
            className="bg-[#B5A58A] text-[#1F1E1B] rounded-full px-6 py-3 font-mono-label text-[10px] tracking-[0.2em] uppercase hover:bg-[#c4b59a] disabled:opacity-40 transition-colors duration-300"
          >
            Post
          </button>
        </div>
      </div>
    </div>
  );
}
